import { FC, FormEvent, MouseEventHandler, useCallback, useContext } from 'react';
import {
  LabelText,
  LoadingWrapper,
  NonMobileWrapper,
  SmsFeeHint,
  TooltipWrapper,
  ValueText,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.styled';
import {
  BKJTextInput,
  BKJButton,
  Flex,
  BKJComboBox,
  BKJLoadingSpinner,
  BKJCheckbox,
  BKJIconTooltip,
  BKJPillDropdown,
  BKJSelect,
  BKJPhoneNumberInput,
} from '@bookjane2/bookjane-design-library';
import { H3 } from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.styled';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useFetchJaneByIdDataContext } from 'modals/JaneDetailsModal/FetchJaneById.data.context';
import {
  EditJaneViewFormProvider,
  useEditJaneViewFormContext,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.form.context';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { TeamLocationFilterInput } from 'modals/JaneDetailsModal/views/EditJaneView/TeamLocationFilterInput';
import { SelectInputPositionByDepartment } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment';
import {
  employmentStatuses,
  RenderOptions,
  monthToDaysMap,
  SeniorityLengthInput,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { janeDetailsModalRoutingService } from 'modals/JaneDetailsModal/JaneDetailsModal.constants';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { useSelector } from 'react-redux';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { SecondaryLocationSection } from 'modals/JaneDetailsModal/views/EditJaneView/SecondaryLocationSection';
import { startCase } from 'utils/startCase';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { CommunitySeniorityMethodContext } from 'pages/TeamMembers/Internal/CommunitySeniorityMethod.context';
import { SeniorityMethodEnum } from 'types/graphql';
import { useTranslation } from 'hooks/useTranslation';
import { AuthRouteDataProvider } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { SelectInputPositionByDepartmentProvider } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { getPhoneMaxLengthByCountry } from 'utils/getPhoneMaxLength';
import { useHasMarketplaceSelector } from 'store/selectors/organizationSelectors/orgSettingsSelector';
import { SkillsSelectInput, useCommunitySkills } from 'components/SkillsSelectInput';

const Component: FC = () => {
  const location = getCurrentlySelectedLocation();
  const {
    values,
    onChange,
    fieldErrors,
    apiErrors,
    isDisabled,
    isSubmitting,
    setSelectMonth,
    dayOptions,
    onSubmit,
    secondaryPositionsDropdownItems,
    numberOfPositionsDeleted,
    setShowDeactivateConfirmModal,
    showDeactivateConfirmModal,
    handlePrimaryPosition,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
    handleRemovedSecondaryPosition,
    handleSecondaryPosition,
    handlePrimaryLocationChange,
    canHoldSkills,
  } = useEditJaneViewFormContext();

  const { data } = useFetchJaneByIdDataContext();
  const emailAvailable = Boolean(data?.emailAvailable);
  const smsAvailable = Boolean(data?.smsAvailable);

  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);

  const { t } = useTranslation();

  const hasMarketplaceAccess = useHasMarketplaceSelector();
  const { options: skillOptions } = useCommunitySkills(
    canHoldSkills ? Number(values?.location?.value) : undefined,
  );

  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(location?.value)),
  );

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );
  const isMultiLocationCallOutFeatureEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(location?.value)),
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectMonth(e.target.value);
      if (values.birthDay) {
        values.birthDay = '';
      }
    },
    [setSelectMonth, values],
  );

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      padding="32px 40px 0"
      borderTop="1px solid rgba(139, 150, 163, 0.33)"
    >
      <BKJCenteredDialog
        isOpen={showDeactivateConfirmModal}
        variant="Danger"
        title={
          numberOfPositionsDeleted > 1
            ? t('team_member:Remove_Positions')
            : t('team_member:Remove_Position')
        }
        text={
          numberOfPositionsDeleted > 1
            ? t('team_member:Remove Position Details')
            : t('team_member:Remove Position Detail')
        }
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('team_member:Remove')}
        onAction={() => {
          handleRemovedSecondaryPosition({} as FormEvent<HTMLFormElement>);
        }}
        onCancel={() => setShowDeactivateConfirmModal(false)}
        onClose={() => setShowDeactivateConfirmModal(false)}
      />
      <Flex flexDirection="column" gap="32px" padding="0 0 50px" width="100%">
        <H3>{t('team_member:Edit Profile')}</H3>
        <BKJTextInput
          label={t('team_member:FIRST_NAME') + '*'}
          onChange={onChange}
          value={values?.firstName}
          name={'firstName'}
          autoComplete="off"
          error={fieldErrors.firstName}
        />
        <BKJTextInput
          label={t('team_member:LAST_NAME') + '*'}
          onChange={onChange}
          value={values?.lastName}
          name={'lastName'}
          autoComplete="off"
          error={fieldErrors.lastName}
        />
        <Flex flexDirection="column" gap="8px">
          <Flex alignItems="center">
            <LabelText>{t('team_member:EMAIL')}</LabelText>*
          </Flex>
          <ValueText style={{ marginLeft: '16px' }}>{values?.email}</ValueText>
        </Flex>
        <Flex flexDirection="column">
          <BKJPhoneNumberInput
            label={t('team_member:MOBILE_PHONE') + '*'}
            onChange={onChange}
            value={values?.phone}
            name={'phone'}
            autoComplete="off"
            error={fieldErrors.phone || apiErrors?.data?.error_messages?.phone}
            maxLength={getPhoneMaxLengthByCountry(location?.country)}
          />
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_ESSENTIAL,
              UserPermissionEnum.USER_TYPE_PREMIUM,
            ]}
          >
            <Flex alignItems="center" gap="10px" style={{ marginTop: '10px', marginLeft: '16px' }}>
              <BKJCheckbox
                variant="PurpleSolid"
                value={values?.isMobile}
                name={'isMobile'}
                onChange={onChange}
              />
              <NonMobileWrapper>{t('team_member:FLAG_AS_NON_APP_USER')}</NonMobileWrapper>
              <BKJIconTooltip iconName="Info" placement="bottom">
                <TooltipWrapper>
                  {t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                </TooltipWrapper>
              </BKJIconTooltip>
            </Flex>
          </UserGuard>
        </Flex>
        {(emailAvailable || smsAvailable) && (
          <Flex flexDirection="column" gap="8px">
            <LabelText>{t('team_member:SHIFT_AVAILABILITY_NOTIFICATION_METHOD')}*</LabelText>
            {emailAvailable && (
              <Flex
                alignItems="center"
                gap="10px"
                width="fit-content"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  onChange({
                    target: { name: 'shiftEmailEnabled', value: !values?.shiftEmailEnabled },
                  })
                }
              >
                <BKJCheckbox
                  variant="PurpleSolid"
                  value={values?.shiftEmailEnabled}
                  name="shiftEmailEnabled"
                  readOnly
                />
                <NonMobileWrapper>{t('team_member:EMAIL')}</NonMobileWrapper>
              </Flex>
            )}
            {smsAvailable && (
              <Flex
                alignItems="center"
                gap="10px"
                width="fit-content"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  onChange({
                    target: { name: 'shiftSmsEnabled', value: !values?.shiftSmsEnabled },
                  })
                }
              >
                <BKJCheckbox
                  variant="PurpleSolid"
                  value={values?.shiftSmsEnabled}
                  name="shiftSmsEnabled"
                  readOnly
                />
                <NonMobileWrapper>
                  {t('team_member:SMS')}{' '}
                  <SmsFeeHint>{t('team_member:SMS_MESSAGING_FEES_HINT')}</SmsFeeHint>
                </NonMobileWrapper>
              </Flex>
            )}
          </Flex>
        )}
        {isMultiLocationTieringEnabled && (
          <Flex flexDirection="column" gap="8px">
            <LabelText>{t('team_member:SENIORITY_LENGTH')}</LabelText>
            {seniorityMethod === SeniorityMethodEnum.years_months_days ? (
              <Flex padding="0 0 10px 0" gap="8px">
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityYears"
                    onChange={onChange}
                    value={values.seniorityYears}
                    label={t('team_member:YEARS').toLowerCase()}
                    upperLimit={99}
                    placeholder={t('team_member:YEARS')}
                  />
                </Flex>
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityMonths"
                    onChange={onChange}
                    value={values.seniorityMonths}
                    label={t('team_member:MONTHS').toLowerCase()}
                    upperLimit={11}
                    placeholder={t('team_member:MONTHS')}
                  />
                </Flex>
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityDays"
                    onChange={onChange}
                    value={values.seniorityDays}
                    label={t('team_member:DAYS').toLowerCase()}
                    upperLimit={30}
                    placeholder={t('team_member:DAYS')}
                  />
                </Flex>
              </Flex>
            ) : (
              <Flex gap="10px">
                <SeniorityLengthInput
                  name="seniorityHours"
                  onChange={onChange}
                  value={values.seniorityHours}
                  label="hours"
                  placeholder="Hours"
                />
              </Flex>
            )}
          </Flex>
        )}
        {WhiteLabelAccessor.isPetalProvider && (
          <Flex flexDirection="column">
            <LabelText>{t('team_member:SELECT_A_LANGUAGE_PREFERENCE') + '*'}</LabelText>
            <BKJRadioInput
              name="locale"
              value="en"
              onChange={onChange}
              checked={values?.locale === 'en'}
              label={t('team_member:ENGLISH')}
            />
            <BKJRadioInput
              name="locale"
              value="fr"
              onChange={onChange}
              checked={values?.locale === 'fr'}
              label={t('team_member:FRENCH')}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:PRIMARY_LOCATION') + '*'}
          </LabelText>
          <TeamLocationFilterInput
            value={values?.location}
            onChange={handlePrimaryLocationChange}
            name="location"
            width="470px"
          />
        </Flex>
        {isMultiLocationSchedulingEnabled && (
          <SecondaryLocationSection
            dropdownName="selectedSecondaryLocationsScheduling"
            dropdownValue={values?.selectedSecondaryLocationsScheduling}
            onChange={onChange}
            dropdownTitle={t('team_member:SECONDARY_LOCATIONS_SCHEDULING')}
            dropdownTooltipInfo={t('team_member:SECONDARY_LOCATIONS_TOOLTIP_INFO_SCHEDULING')}
            withEnabledFeatures={['MULTI_LOCATION_SCHEDULING']}
            selectedPrimaryLocationId={values?.location?.value?.toString()}
          />
        )}
        {(isMultiLocationTieringEnabled || isMultiLocationCallOutFeatureEnabled) && (
          <SecondaryLocationSection
            dropdownName="selectedSecondaryLocationsCallOut"
            dropdownValue={values?.selectedSecondaryLocationsCallOut}
            onChange={onChange}
            dropdownTitle={t('team_member:SECONDARY_LOCATIONS_CALL_OUT')}
            dropdownTooltipInfo={t('team_member:SECONDARY_LOCATIONS_TOOLTIP_INFO_CALL_OUT')}
            withEnabledFeatures={
              isMultiLocationTieringEnabled
                ? ['MULTI_LOCATION_TIERING']
                : ['MULTI_LOCATION_CALLOUT']
            }
            withoutEnabledFeatures={isMultiLocationTieringEnabled ? [] : ['MULTI_LOCATION_TIERING']}
            selectedPrimaryLocationId={values?.location?.value?.toString()}
          />
        )}
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:PRIMARY_POSITION') + '*'}
          </LabelText>
          <SelectInputPositionByDepartment
            dropdownVariant="Square"
            placeholder={t('team_member:SELECT_POSITION')}
            onChange={handlePrimaryPosition}
            type="Single"
            name="position_id"
            variant="GreyOutlined"
            value={values?.position_id}
            error={fieldErrors?.position_id}
            width="100%"
            key={`${values?.location?.value}`}
            allocatedToCommunities={[`${values?.location?.value}`]}
            dropdownProps={{
              dropdownListItemVariant: 'List',
              isSearchable: false,
            }}
          />
        </Flex>
        <Flex flexDirection="column">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:SECONDARY_POSITIONS')}
          </LabelText>
          <BKJPillDropdown
            onChange={handleSecondaryPosition}
            name="selectedSecondaryPositions"
            value={values?.selectedSecondaryPositions}
            key={`${values?.location?.value}`}
            options={secondaryPositionsDropdownItems}
            validations={[]}
            error={fieldErrors.selectedSecondaryPositions}
            placeholder={t('team_member:SELECT_POSITIONS')}
            max={secondaryPositionsDropdownItems?.length || 0}
          />
        </Flex>
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_ESSENTIAL,
            UserPermissionEnum.USER_TYPE_PREMIUM,
          ]}
        >
          <BKJComboBox
            label={t('team_member:EMPLOYMENT_STATUS') + '*'}
            placeholder={t('team_member:SELECT_STATUS')}
            name="employmentStatus"
            value={[startCase(values?.employmentStatus)]}
            onChange={onChange}
            options={employmentStatuses()}
            type="Single"
            action="OpenToggle"
            filter="TimeStartsWith"
            error={fieldErrors?.employmentStatus}
          />
          {canHoldSkills && (
            <SkillsSelectInput
              name="selectedSkills"
              value={values?.selectedSkills}
              onChange={onChange}
              options={skillOptions}
              label={t('team_member:SKILLS')}
              placeholder={t('team_member:SELECT_SKILLS')}
              max={skillOptions.length || undefined}
            />
          )}
          {!hasMarketplaceAccess && (
            <BKJTextInput
              label={t('team_member:HOURLY_RATE')}
              onChange={onChange}
              value={`${values?.hourlyRate ? values?.hourlyRate : ''}`}
              name={'hourlyRate'}
              autoComplete="off"
            />
          )}
        </UserGuard>
        <BKJTextInput
          label={t('team_member:EMPLOYEE_ID')}
          onChange={onChange}
          value={values?.employeeId}
          name={'employeeId'}
          autoComplete="off"
          error={fieldErrors?.employeeId}
        />
        <Flex flexDirection="column" gap="8px">
          <LabelText>{t('team_member:EMPLOYEE_START_DATE')}</LabelText>
          <ShiftDateCalendarInput
            name="employeeStartDate"
            value={values?.employeeStartDate}
            onChange={onChange}
            width="100%"
            error={fieldErrors?.employeeStartDate}
          />
        </Flex>
        <Flex gap="16px">
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_MONTH')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:SELECT_MONTH')}
              name="birthMonth"
              options={Object.keys(monthToDaysMap).map((month, index) => ({
                key: index,
                id: index,
                value: month,
                label: t(`team_member:${month}`),
              }))}
              onChange={(e) => {
                handleChange(e);
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthMonth}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthMonth}
            />
          </Flex>
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_DAY')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:DAY')}
              name="birthDay"
              options={dayOptions}
              onChange={(e) => {
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthDay}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthDay}
            />
          </Flex>
        </Flex>
      </Flex>

      <BKJSideModalFooter>
        <Flex width="100%" justifyContent="end">
          <BKJButton
            variant="PurpleOutlined"
            width="140px"
            onClick={() => {
              setIsDiscardConfirmationOpen(true);
              janeDetailsModalRoutingService?.isOpen();
            }}
            type="button"
          >
            {t('team_member:CANCEL')}
          </BKJButton>
          <BKJButton
            variant="PurpleSolid"
            disabled={isDisabled || isSubmitting}
            isLoading={isSubmitting}
            width="140px"
            onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
            type="button"
            style={{ marginLeft: '4%' }}
          >
            {t('auth:SAVE')}
          </BKJButton>
        </Flex>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={isDiscardConfirmationOpen}
        variant="Danger"
        title={t('create_shifts:DISCARD_CHANGES')}
        data-cy="discard-modal"
        text=""
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('create_shifts:DISCARD')}
        onAction={() => {
          setIsDiscardConfirmationOpen(false);
          return janeDetailsModalRoutingService.goToPrevView();
        }}
        onCancel={() => setIsDiscardConfirmationOpen(false)}
        onClose={() => setIsDiscardConfirmationOpen(false)}
      />
    </Flex>
  );
};

export const EditJaneDefaultFormComponent: FC = () => {
  const { data, isLoading } = useFetchJaneByIdDataContext();

  if (isLoading)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );
  return (
    <AuthRouteDataProvider>
      <SelectInputPositionByDepartmentProvider
        allocatedToCommunities={[data?.primaryCommunity?.id]}
      >
        <EditJaneViewFormProvider>
          <Component />
        </EditJaneViewFormProvider>
      </SelectInputPositionByDepartmentProvider>
    </AuthRouteDataProvider>
  );
};
export const EditJaneView: FC = () => {
  return <EditJaneDefaultFormComponent />;
};
